// FormatterPro — grammar analysis client.
//
// Changes from the previous version:
//  - Sends the Hub token (same key api.js uses), so the server can confirm who is asking.
//  - Reads the fields analyze-grammar.php actually returns: the flagged text is cut
//    from the manuscript by offset, suggestions come from `replacements`, and the
//    category comes from LanguageTool's issueType/category id (not just its display name).
//  - Surfaces partial scans (`partial`, `unchecked`) so the UI can say what wasn't checked.

const HUB_TOKEN_KEY = 'gospelwise_handoff_token';

const getHubToken = () => {
  for (const store of [() => sessionStorage, () => localStorage]) {
    try {
      const token = store().getItem(HUB_TOKEN_KEY);
      if (token) return token;
    } catch (error) {
      // Storage can be blocked (private mode); fall through.
    }
  }
  return '';
};

const authHeaders = (extra = {}) => {
  const token = getHubToken();
  if (!token) {
    throw new Error('Please open FormatterPro from the Hub to run the grammar check.');
  }
  return {
    ...extra,
    Authorization: `Bearer ${token}`,
    // Backup copy: NameHero/LiteSpeed can strip Authorization.
    'X-Hub-Token': token,
  };
};

const findMatchText = (match) => {
  const candidates = [match.match, match.text, match.original];
  return candidates.find(value => typeof value === 'string' && value.length > 0) || '';
};

const extractDocxText = async (file) => {
  if (!file || !/\.docx$/i.test(file.name || '')) {
    throw new Error('In-memory grammar analysis supports .docx drafts. Save legacy .doc files as .docx or upload the manuscript first.');
  }

  const mammothModule = await import('mammoth/mammoth.browser');
  const mammoth = mammothModule.default || mammothModule;
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });
  const htmlWithParagraphBreaks = result.value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '$&\n');

  return new DOMParser()
    .parseFromString(htmlWithParagraphBreaks, 'text/html')
    .body.textContent
    .replace(/ /g, ' ')
    .replace(/\r\n?/g, '\n')
    .trim();
};

// LanguageTool issueType / category id → FormatterPro's five review buckets.
const ISSUE_TYPE_MAP = {
  misspelling: 'spelling',
  typographical: 'punctuation',
  whitespace: 'punctuation',
  grammar: 'grammar',
  inconsistency: 'grammar',
  duplication: 'style',
  style: 'style',
  register: 'style',
  'locale-violation': 'spelling',
};
const CATEGORY_ID_MAP = {
  TYPOS: 'spelling',
  CASING: 'spelling',
  GRAMMAR: 'grammar',
  CONFUSED_WORDS: 'grammar',
  PUNCTUATION: 'punctuation',
  TYPOGRAPHY: 'punctuation',
  STYLE: 'style',
  REDUNDANCY: 'style',
  REPETITIONS_STYLE: 'style',
  COLLOCATIONS: 'clarity',
  SEMANTICS: 'clarity',
  PLAIN_ENGLISH: 'clarity',
};
const BUCKETS = ['spelling', 'grammar', 'punctuation', 'style', 'clarity'];

const categorize = (match) => {
  const issueType = String(match.issueType || '').toLowerCase();
  if (ISSUE_TYPE_MAP[issueType]) return ISSUE_TYPE_MAP[issueType];

  const categoryId = String(match.categoryId || '').toUpperCase();
  if (CATEGORY_ID_MAP[categoryId]) return CATEGORY_ID_MAP[categoryId];

  const label = String(match.category || match.type || '').toLowerCase();
  if (label.includes('typo')) return 'spelling';
  return BUCKETS.find(item => label.includes(item)) || 'general';
};

export const normalizeGrammarMatches = (matches, manuscriptContent) => {
  let searchFrom = 0;

  return matches.map((match, index) => {
    const quoted = findMatchText(match);
    const rawStart = match.start ?? match.start_offset ?? match.offset;
    const rawEnd = match.end ?? match.end_offset;
    let start = Number.isInteger(Number(rawStart)) && rawStart !== null ? Number(rawStart) : -1;
    let end = Number.isInteger(Number(rawEnd)) && rawEnd !== null ? Number(rawEnd) : -1;

    if (start < 0 && quoted) {
      start = manuscriptContent.indexOf(quoted, searchFrom);
      if (start < 0) start = manuscriptContent.indexOf(quoted);
    }
    if (end < 0 && start >= 0) {
      const length = Number(match.length);
      end = Number.isInteger(length) && length > 0 ? start + length : start + quoted.length;
    }
    if (start >= 0 && end > manuscriptContent.length) end = manuscriptContent.length;
    if (start >= 0 && end > start) searchFrom = end;

    const replacements = Array.isArray(match.replacements)
      ? match.replacements.filter(value => typeof value === 'string').slice(0, 5)
      : [];
    const text = start >= 0 && end > start ? manuscriptContent.slice(start, end) : quoted;
    const message = match.message || match.shortMessage || match.description || 'Review this passage in context.';
    const excerptStart = start >= 0 ? Math.max(0, start - 55) : 0;
    const excerptEnd = start >= 0 ? Math.min(manuscriptContent.length, end + 55) : 0;

    return {
      id: String(match.id ?? `${match.ruleId || 'match'}-${start}-${index}`),
      start,
      end,
      text,
      category: categorize(match),
      ruleId: match.ruleId || '',
      message: String(message),
      suggestion: typeof match.suggestion === 'string' ? match.suggestion : (replacements[0] || ''),
      replacements,
      excerpt: start >= 0 ? manuscriptContent.slice(excerptStart, excerptEnd) : '',
    };
  });
};

const readJson = async (response) => response.json().catch(() => null);

export const runGrammarAnalysis = async (manuscriptIdOrOptions) => {
  const options = manuscriptIdOrOptions && typeof manuscriptIdOrOptions === 'object'
    ? manuscriptIdOrOptions
    : { manuscriptId: manuscriptIdOrOptions };
  const {
    manuscriptId,
    manuscriptFile,
    manuscriptContent: suppliedContent,
    onContentReady,
    language,
  } = options;
  let manuscriptContent = typeof suppliedContent === 'string' ? suppliedContent : '';
  const hasSavedId = manuscriptId && String(manuscriptId) !== 'new';

  if (!manuscriptContent && hasSavedId) {
    const textResponse = await fetch(
      `/api/manuscript-text.php?manuscript_id=${encodeURIComponent(manuscriptId)}`,
      { credentials: 'same-origin', headers: authHeaders() },
    );
    const textData = await readJson(textResponse);

    if (!textResponse.ok || !textData?.success || typeof textData.text !== 'string') {
      throw new Error(textData?.error || 'Could not load manuscript text for analysis.');
    }
    manuscriptContent = textData.text;
  } else if (!manuscriptContent && manuscriptFile) {
    manuscriptContent = await extractDocxText(manuscriptFile);
  }

  if (!manuscriptContent.trim()) {
    throw new Error('No manuscript text is available to analyze. Upload or select a .docx manuscript, or provide draft text.');
  }

  if (typeof onContentReady === 'function') {
    onContentReady(manuscriptContent);
  }

  const response = await fetch('/api/analyze-grammar.php', {
    method: 'POST',
    credentials: 'same-origin',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ text: manuscriptContent, ...(language ? { language } : {}) }),
  });
  const result = await readJson(response);

  if (!response.ok) {
    throw new Error(result?.error || `Grammar checker returned HTTP ${response.status}.`);
  }
  if (!Array.isArray(result?.matches)) {
    throw new Error('The grammar checker response did not include a matches array.');
  }

  const unchecked = Array.isArray(result.unchecked)
    ? result.unchecked.map(range => ({
      start: Number(range.offset) || 0,
      end: (Number(range.offset) || 0) + (Number(range.length) || 0),
    }))
    : [];

  return {
    manuscriptContent,
    matches: normalizeGrammarMatches(result.matches, manuscriptContent),
    // When partial is true, show a notice such as:
    // "Part of your manuscript couldn't be checked. Run the scan again to finish it."
    partial: result.partial === true,
    unchecked,
  };
};